import React, { createContext, useContext, useReducer, ReactNode, useCallback } from 'react';
import { aiPredictionsAPI } from '../services/api';
import { useAuth } from './AuthContext';

// Types
export interface PredictionData {
  current: number;
  predicted: number;
  growth: number;
}

export interface PredictionCategory {
  title: string;
  description: string;
  data: {
    [key: string]: PredictionData;
  };
}

export interface ChartData {
  labels: string[];
  datasets: {
    label: string;
    data: number[];
    backgroundColor?: string | string[];
    borderColor?: string | string[];
    borderWidth?: number;
    tension?: number;
    pointBackgroundColor?: string;
    pointBorderColor?: string;
    pointRadius?: number;
    pointHoverRadius?: number;
  }[];
}

export interface AIInsight {
  type: 'insight' | 'prediction' | 'warning' | 'recommendation';
  title: string;
  message: string;
  icon: string;
  color: string;
}

export interface AIAnalysis {
  insights: AIInsight[];
}

export interface AIPredictionsState {
  predictions: {
    growth: PredictionCategory | null;
    engagement: PredictionCategory | null;
    content: PredictionCategory | null;
    audience: PredictionCategory | null;
  };
  charts: {
    [key: string]: ChartData | null;
  };
  aiAnalysis: {
    [key: string]: AIAnalysis | null;
  };
  loading: {
    predictions: boolean;
    charts: boolean;
    aiAnalysis: {
      [key: string]: boolean;
    };
  };
  error: {
    predictions: string | null;
    charts: string | null;
    aiAnalysis: {
      [key: string]: string | null;
    };
  };
}

export type AIPredictionsAction =
  | { type: 'FETCH_PREDICTIONS_START' }
  | { type: 'FETCH_PREDICTIONS_SUCCESS'; payload: any }
  | { type: 'FETCH_PREDICTIONS_ERROR'; payload: string }
  | { type: 'FETCH_CHARTS_START' }
  | { type: 'FETCH_CHARTS_SUCCESS'; payload: { chartType: string; data: ChartData } }
  | { type: 'FETCH_CHARTS_ERROR'; payload: string }
  | { type: 'FETCH_AI_ANALYSIS_START'; payload: string }
  | { type: 'FETCH_AI_ANALYSIS_SUCCESS'; payload: { chartType: string; data: AIAnalysis } }
  | { type: 'FETCH_AI_ANALYSIS_ERROR'; payload: { chartType: string; error: string } };

const initialState: AIPredictionsState = {
  predictions: {
    growth: null,
    engagement: null,
    content: null,
    audience: null,
  },
  charts: {},
  aiAnalysis: {},
  loading: {
    predictions: false,
    charts: false,
    aiAnalysis: {},
  },
  error: {
    predictions: null,
    charts: null,
    aiAnalysis: {},
  },
};

const aiPredictionsReducer = (state: AIPredictionsState, action: AIPredictionsAction): AIPredictionsState => {
  switch (action.type) {
    case 'FETCH_PREDICTIONS_START':
      return {
        ...state,
        loading: { ...state.loading, predictions: true },
        error: { ...state.error, predictions: null },
      };
    case 'FETCH_PREDICTIONS_SUCCESS':
      return {
        ...state,
        predictions: action.payload,
        loading: { ...state.loading, predictions: false },
      };
    case 'FETCH_PREDICTIONS_ERROR':
      return {
        ...state,
        loading: { ...state.loading, predictions: false },
        error: { ...state.error, predictions: action.payload },
      };
    case 'FETCH_CHARTS_START':
      return {
        ...state,
        loading: { ...state.loading, charts: true },
        error: { ...state.error, charts: null },
      };
    case 'FETCH_CHARTS_SUCCESS':
      return {
        ...state,
        charts: {
          ...state.charts,
          [action.payload.chartType]: action.payload.data,
        },
        loading: { ...state.loading, charts: false },
      };
    case 'FETCH_CHARTS_ERROR':
      return {
        ...state,
        loading: { ...state.loading, charts: false },
        error: { ...state.error, charts: action.payload },
      };
    case 'FETCH_AI_ANALYSIS_START':
      return {
        ...state,
        loading: {
          ...state.loading,
          aiAnalysis: {
            ...state.loading.aiAnalysis,
            [action.payload]: true,
          },
        },
        error: {
          ...state.error,
          aiAnalysis: {
            ...state.error.aiAnalysis,
            [action.payload]: null,
          },
        },
      };
    case 'FETCH_AI_ANALYSIS_SUCCESS':
      return {
        ...state,
        aiAnalysis: {
          ...state.aiAnalysis,
          [action.payload.chartType]: action.payload.data,
        },
        loading: {
          ...state.loading,
          aiAnalysis: {
            ...state.loading.aiAnalysis,
            [action.payload.chartType]: false,
          },
        },
        error: {
          ...state.error,
          aiAnalysis: {
            ...state.error.aiAnalysis,
            [action.payload.chartType]: null,
          },
        },
      };
    case 'FETCH_AI_ANALYSIS_ERROR':
      return {
        ...state,
        loading: {
          ...state.loading,
          aiAnalysis: {
            ...state.loading.aiAnalysis,
            [action.payload.chartType]: false,
          },
        },
        error: {
          ...state.error,
          aiAnalysis: {
            ...state.error.aiAnalysis,
            [action.payload.chartType]: action.payload.error,
          },
        },
      };
    default:
      return state;
  }
};

interface AIPredictionsContextType {
  state: AIPredictionsState;
  fetchPredictions: () => Promise<void>;
  fetchChartData: (chartType: string) => Promise<void>;
  fetchAIAnalysis: (chartType: string) => Promise<void>;
  fetchAllAIPredictionsData: () => Promise<void>;
}

const AIPredictionsContext = createContext<AIPredictionsContextType | undefined>(undefined);

export const AIPredictionsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(aiPredictionsReducer, initialState);
  const { business } = useAuth();

  const fetchPredictions = useCallback(async () => {
    if (!business?._id) return;
    
    dispatch({ type: 'FETCH_PREDICTIONS_START' });
    try {
      const response = await aiPredictionsAPI.getPredictions(business._id);
      dispatch({ type: 'FETCH_PREDICTIONS_SUCCESS', payload: response.data.data });
    } catch (error: any) {
      if (error.response?.status === 404) {
        dispatch({ type: 'FETCH_PREDICTIONS_ERROR', payload: 'PREPARING' });
      } else {
        dispatch({ type: 'FETCH_PREDICTIONS_ERROR', payload: 'Veri yüklenirken hata oluştu' });
      }
    }
  }, [business?._id]);

  const fetchChartData = useCallback(async (chartType: string) => {
    if (!business?._id) return;
    
    dispatch({ type: 'FETCH_CHARTS_START' });
    try {
      const response = await aiPredictionsAPI.getChartData(business._id, chartType);
      dispatch({
        type: 'FETCH_CHARTS_SUCCESS',
        payload: { chartType, data: response.data.data },
      });
    } catch (error: any) {
      if (error.response?.status === 404) {
        dispatch({ type: 'FETCH_CHARTS_ERROR', payload: 'PREPARING' });
      } else {
        dispatch({ type: 'FETCH_CHARTS_ERROR', payload: 'Veri yüklenirken hata oluştu' });
      }
    }
  }, [business?._id]);

  const fetchAIAnalysis = useCallback(async (chartType: string) => {
    if (!business?._id) return;
    
    try {
      dispatch({ type: 'FETCH_AI_ANALYSIS_START', payload: chartType });
      const response = await aiPredictionsAPI.getAIAnalysis(business._id, chartType);
      dispatch({
        type: 'FETCH_AI_ANALYSIS_SUCCESS',
        payload: { chartType, data: response.data.data },
      });
    } catch (error: any) {
      if (error.response?.status === 404) {
        dispatch({
          type: 'FETCH_AI_ANALYSIS_ERROR',
          payload: { chartType, error: 'PREPARING' },
        });
      } else {
        console.error('AI Analysis fetch error:', error);
        dispatch({
          type: 'FETCH_AI_ANALYSIS_ERROR',
          payload: { chartType, error: 'AI analizi yüklenirken hata oluştu' },
        });
      }
    }
  }, [business?._id]);

  // Tüm AI Predictions verisini tek seferde çekip state'e dağıtan fonksiyon
  const fetchAllAIPredictionsData = useCallback(async () => {
    if (!business?._id) return;
    
    const chartTypes = ['growth', 'engagement', 'content', 'audience'];
    
    try {
      // Tüm loading state'lerini başlat
      dispatch({ type: 'FETCH_PREDICTIONS_START' });
      dispatch({ type: 'FETCH_CHARTS_START' });
      chartTypes.forEach(chartType => {
        dispatch({ type: 'FETCH_AI_ANALYSIS_START', payload: chartType });
      });

      const response = await aiPredictionsAPI.getAllAIPredictionsData(business._id);
      // API'den gelen data bir array, ilk elemanı alıyoruz
      const dataArray = response.data.data;
      const predictionsData = Array.isArray(dataArray) && dataArray.length > 0 ? dataArray[0] : dataArray;

      // Predictions data
      if (predictionsData?.data?.predictions) {
        dispatch({ type: 'FETCH_PREDICTIONS_SUCCESS', payload: predictionsData.data.predictions });
      } else {
        dispatch({ type: 'FETCH_PREDICTIONS_ERROR', payload: 'PREPARING' });
      }

      // Charts data
      if (predictionsData?.data?.charts) {
        chartTypes.forEach(chartType => {
          if (predictionsData.data.charts[chartType]) {
            dispatch({
              type: 'FETCH_CHARTS_SUCCESS',
              payload: { chartType, data: predictionsData.data.charts[chartType] }
            });
          } else {
            dispatch({ type: 'FETCH_CHARTS_ERROR', payload: 'PREPARING' });
          }
        });
      } else {
        dispatch({ type: 'FETCH_CHARTS_ERROR', payload: 'PREPARING' });
      }

      // AI Analysis
      if (predictionsData?.data?.aiAnalysis) {
        chartTypes.forEach(chartType => {
          if (predictionsData.data.aiAnalysis[chartType]) {
            dispatch({
              type: 'FETCH_AI_ANALYSIS_SUCCESS',
              payload: { chartType, data: predictionsData.data.aiAnalysis[chartType] }
            });
          } else {
            dispatch({
              type: 'FETCH_AI_ANALYSIS_ERROR',
              payload: { chartType, error: 'PREPARING' }
            });
          }
        });
      } else {
        chartTypes.forEach(chartType => {
          dispatch({
            type: 'FETCH_AI_ANALYSIS_ERROR',
            payload: { chartType, error: 'PREPARING' }
          });
        });
      }

    } catch (error: any) {
      // Hata durumunda tüm state'leri PREPARING olarak işaretle
      if (error.response?.status === 404) {
        dispatch({ type: 'FETCH_PREDICTIONS_ERROR', payload: 'PREPARING' });
        dispatch({ type: 'FETCH_CHARTS_ERROR', payload: 'PREPARING' });
        chartTypes.forEach(chartType => {
          dispatch({
            type: 'FETCH_AI_ANALYSIS_ERROR',
            payload: { chartType, error: 'PREPARING' }
          });
        });
      } else {
        const errorMessage = error instanceof Error ? error.message : 'Bilinmeyen hata';
        dispatch({ type: 'FETCH_PREDICTIONS_ERROR', payload: errorMessage });
        dispatch({ type: 'FETCH_CHARTS_ERROR', payload: errorMessage });
        chartTypes.forEach(chartType => {
          dispatch({
            type: 'FETCH_AI_ANALYSIS_ERROR',
            payload: { chartType, error: errorMessage }
          });
        });
      }
    }
  }, [business?._id]);

  return (
    <AIPredictionsContext.Provider
      value={{
        state,
        fetchPredictions,
        fetchChartData,
        fetchAIAnalysis,
        fetchAllAIPredictionsData,
      }}
    >
      {children}
    </AIPredictionsContext.Provider>
  );
};

export const useAIPredictions = (): AIPredictionsContextType => {
  const context = useContext(AIPredictionsContext);
  if (context === undefined) {
    throw new Error('useAIPredictions must be used within a AIPredictionsProvider');
  }
  return context;
};
